import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { api } from "../lib/api";

export interface FilterState {
  brandIds: string[];
  retailerIds: string[];
  days: number;
  changeTypes: string[];
}

export interface FilterOption {
  value: string;
  label: string;
  domain: string | null;
}

const DEFAULT_DAYS = 30;

function parseSearchParams(params: URLSearchParams): Partial<FilterState> {
  const result: Partial<FilterState> = {};
  const brands = params.get("brands");
  if (brands) result.brandIds = brands.split(",").filter(Boolean);
  const retailers = params.get("retailers");
  if (retailers) result.retailerIds = retailers.split(",").filter(Boolean);
  const days = params.get("days");
  if (days) result.days = parseInt(days, 10) || DEFAULT_DAYS;
  const changeTypes = params.get("change_type");
  if (changeTypes) result.changeTypes = changeTypes.split(",").filter(Boolean);
  return result;
}

function toSearchParamString(state: FilterState): string {
  const params = new URLSearchParams();
  if (state.brandIds.length > 0) params.set("brands", state.brandIds.join(","));
  if (state.retailerIds.length > 0) params.set("retailers", state.retailerIds.join(","));
  if (state.days !== DEFAULT_DAYS) params.set("days", String(state.days));
  if (state.changeTypes.length > 0) params.set("change_type", state.changeTypes.join(","));
  return params.toString();
}

export function useFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<FilterState>(() => ({
    brandIds: [],
    retailerIds: [],
    days: DEFAULT_DAYS,
    changeTypes: [],
    ...parseSearchParams(searchParams),
  }));

  const [brandOptions, setBrandOptions] = useState<FilterOption[]>([]);
  const [retailerOptions, setRetailerOptions] = useState<FilterOption[]>([]);

  // Track previous URL string to avoid infinite loops
  const prevUrlRef = useRef(toSearchParamString(filters));

  // Load filter options once
  useEffect(() => {
    api
      .get("/brands", { params: { page_size: 100 } })
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : data.items ?? data.data ?? [];
        setBrandOptions(
          items.map((b: { id: string; name: string; domain: string | null }) => ({
            value: b.id,
            label: b.name,
            domain: b.domain,
          })),
        );
      })
      .catch(() => {
        notifications.show({ color: "orange", message: "Failed to load brand filters" });
      });

    api
      .get("/retailers", { params: { page_size: 100 } })
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : data.items ?? data.data ?? [];
        setRetailerOptions(
          items.map((r: { id: string; name: string; domain: string | null }) => ({
            value: r.id,
            label: r.name,
            domain: r.domain,
          })),
        );
      })
      .catch(() => {
        notifications.show({ color: "orange", message: "Failed to load retailer filters" });
      });
  }, []);

  // Sync filter state to URL — only when the serialized string actually changes
  useEffect(() => {
    const next = toSearchParamString(filters);
    if (next === prevUrlRef.current) return;
    prevUrlRef.current = next;
    setSearchParams(next ? new URLSearchParams(next) : {}, { replace: true });
  }, [filters, setSearchParams]);

  // Use a ref for stable toApiParams — sync in effect, not render
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const setBrands = useCallback(
    (brandIds: string[]) => setFilters((prev) => ({ ...prev, brandIds })),
    [],
  );

  const setRetailers = useCallback(
    (retailerIds: string[]) => setFilters((prev) => ({ ...prev, retailerIds })),
    [],
  );

  const setDays = useCallback(
    (days: number) => setFilters((prev) => ({ ...prev, days })),
    [],
  );

  const setChangeTypes = useCallback(
    (changeTypes: string[]) => setFilters((prev) => ({ ...prev, changeTypes })),
    [],
  );

  const clearAll = useCallback(
    () =>
      setFilters({
        brandIds: [],
        retailerIds: [],
        days: DEFAULT_DAYS,
        changeTypes: [],
      }),
    [],
  );

  /** Convert current filter state to API query params — stable reference */
  const toApiParams = useCallback(() => {
    const f = filtersRef.current;
    const params: Record<string, unknown> = {};
    if (f.brandIds.length > 0) params.brand_id = f.brandIds;
    if (f.retailerIds.length > 0) params.retailer_id = f.retailerIds;
    const end = new Date();
    const start = new Date(end.getTime() - f.days * 86_400_000);
    params.start_date = start.toISOString().slice(0, 10);
    params.end_date = end.toISOString().slice(0, 10);
    if (f.changeTypes.length > 0) params.change_type = f.changeTypes;
    return params;
  }, []);

  // Derived filter key — changes when any filter changes, usable as effect dependency
  const filterKey = useMemo(
    () =>
      `${filters.brandIds.join(",")}_${filters.retailerIds.join(",")}_${filters.days}_${filters.changeTypes.join(",")}`,
    [filters.brandIds, filters.retailerIds, filters.days, filters.changeTypes],
  );

  const hasActiveFilters =
    filters.brandIds.length > 0 ||
    filters.retailerIds.length > 0 ||
    filters.days !== DEFAULT_DAYS ||
    filters.changeTypes.length > 0;

  return {
    filters,
    brandOptions,
    retailerOptions,
    setBrands,
    setRetailers,
    setDays,
    setChangeTypes,
    clearAll,
    toApiParams,
    hasActiveFilters,
    filterKey,
  };
}
